/**
 * Persistence of the client's backend credentials, one entry per backend origin (config.ts backendOrigin): the guest
 * device token, the device session's refresh token and its current access token. Windows of the same origin share the
 * entry: a window that refreshes stores the new pair, and the others take it from here instead of reusing their old
 * refresh token (BackendClient).
 */
import type { WebStorageLike } from '../../server/core/storage-local';

export interface StoredCredentials {
  /** Guest device token issued by `auth.guest` (keeps the guest account across sessions). */
  deviceToken?: string;
  /** Current access token (the protocol 1 name: an older client kept its session token here). */
  sessionToken?: string;
  /** Server-time expiry of the access token. */
  accessExpiresAt?: number;
  /** Refresh token of the device session (`auth.refresh`; rotated on every refresh). */
  refreshToken?: string;
  /** Server-time expiry of the refresh token. */
  refreshExpiresAt?: number;
  /** Device session the tokens belong to. */
  sessionId?: string;
  playerId?: string;
}

export interface CredentialStore {
  load(): StoredCredentials;
  save(c: StoredCredentials): void;
  clear(): void;
}

export class MemoryCredentialStore implements CredentialStore {
  private c: StoredCredentials = {};
  constructor(initial: StoredCredentials = {}) {
    this.c = { ...initial };
  }
  load(): StoredCredentials {
    return { ...this.c };
  }
  save(c: StoredCredentials): void {
    this.c = { ...c };
  }
  clear(): void {
    this.c = {};
  }
}

/** Web Storage key of the offline (in-process) backend's credentials; kept so existing offline progress stays. */
export const OFFLINE_CREDENTIALS_KEY = 'fwp.backend.credentials';

/**
 * Web Storage key for the credentials of one backend: `fwp.backend.credentials:<origin>` for a network backend
 * (see config.ts backendOrigin), the historical key for the offline backend. Separate keys mean a session or
 * device token issued by one server is never sent to another one.
 */
export function credentialsKey(origin: string | null): string {
  return origin ? `${OFFLINE_CREDENTIALS_KEY}:${origin}` : OFFLINE_CREDENTIALS_KEY;
}

/** Stores credentials as one JSON entry in Web Storage (browser `localStorage`). */
export class WebStorageCredentialStore implements CredentialStore {
  constructor(
    private readonly storage: WebStorageLike,
    readonly key = OFFLINE_CREDENTIALS_KEY,
  ) {}
  load(): StoredCredentials {
    try {
      const raw = this.storage.getItem(this.key);
      return raw ? (JSON.parse(raw) as StoredCredentials) : {};
    } catch {
      return {};
    }
  }
  save(c: StoredCredentials): void {
    try {
      this.storage.setItem(this.key, JSON.stringify(c));
    } catch {
      /* storage full or unavailable: credentials stay in memory only */
    }
  }
  clear(): void {
    try {
      this.storage.removeItem(this.key);
    } catch {
      /* ignore */
    }
  }
}
